import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@accure/ui';
import { z } from 'zod';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';

import { CreateUser } from '../api/mutations/create-user';
import type { TaskUser } from '../entities/task-user';

export type AddUserDialogProps = {
  onClose: (user?: TaskUser) => void;
} & Pick<DialogProps, 'open'>;

export function AddUserDialog({ onClose, ...props }: AddUserDialogProps) {
  const form = useForm<FormCreateInputs>();
  const addUser = CreateUser.useMutation();
  const { isValid } = form.formState;

  async function submitForm(data: FormCreateInputs) {
    const result = await toast.promise(addUser.mutateAsync(data), {
      success: 'User added',
      error: 'User could not be added',
    });

    onClose?.(result);
    form.reset();
  }

  return (
    <Dialog onClose={() => onClose?.()} {...props} className="w-96">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submitForm)} autoComplete="off">
          <DialogTitle>Add new user</DialogTitle>
          <DialogDescription>
            Add a new user and assign them to the task.
          </DialogDescription>
          <DialogBody className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First and last name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button disabled={addUser.isLoading || !isValid} type="submit">
              Add user
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

export const formCreateSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  company: z.string().min(1),
});

export type FormCreateInputs = z.infer<typeof formCreateSchema>;
